import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import type { DocumentAsset } from '../../../services/admin/adminBackendServiceDocumentLibraryMixin';

export function EditDocumentDialog({
  doc,
  onCancel,
  onSave,
}: {
  doc: DocumentAsset | null;
  onCancel: () => void;
  onSave: (externalId: string, fields: { title: string; description: string }) => Promise<void>;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (doc) {
      setTitle(doc.title ?? '');
      setDescription(doc.description ?? '');
    }
  }, [doc]);

  const handleSave = async () => {
    if (!doc) return;
    setSaving(true);
    try {
      await onSave(doc.externalId, { title: title.trim(), description: description.trim() });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!doc} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Edit document</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <TextField
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          disabled={saving}
          autoFocus
        />
        <TextField
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          disabled={saving}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="inherit" disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !title.trim()}
          startIcon={saving ? <CircularProgress size={14} /> : undefined}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function DeleteDocumentDialog({
  doc,
  onCancel,
  onConfirm,
}: {
  doc: DocumentAsset | null;
  onCancel: () => void;
  onConfirm: (force: boolean) => void;
}) {
  const uses = doc?.referenceCount ?? 0;
  return (
    <Dialog open={!!doc} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Delete document?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {uses > 0
            ? `This document is referenced in ${uses} place${uses === 1 ? '' : 's'}. Delete it anyway?`
            : 'Delete this document from the library? This can\u2019t be undone.'}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="inherit">
          Cancel
        </Button>
        <Button color="error" variant="contained" onClick={() => onConfirm(uses > 0)}>
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
}
